import * as XLSX from 'xlsx';
import { EntitySchema, FieldSchema } from '../config/etl-schemas';

/**
 * Genera y descarga un archivo Excel plantilla para una entidad.
 * El archivo tiene 3 filas iniciales:
 *   1. Nombre del campo (key técnica que el backend espera)
 *   2. Tipo de dato esperado + opciones (si aplica)
 *   3. Ejemplo (marcado con "EJEMPLO - BORRAR ESTA FILA")
 * Los datos reales deben empezar en la fila 4.
 */
export function downloadEntityTemplate(schema: EntitySchema): void {
  const fieldKeys = schema.fields.map(f => f.key);

  // Fila 1: nombre del campo (key que el backend espera)
  const headerRow = fieldKeys;

  // Fila 2: tipo de dato esperado + opciones
  const typeRow = schema.fields.map(field => buildTypeLabel(field));

  // Fila 3: ejemplo
  const exampleRow = schema.fields.map(field => field.example);

  // Construir la matriz de datos
  const data: (string | number)[][] = [
    headerRow,
    typeRow,
    exampleRow,
  ];

  // Crear la hoja
  const worksheet = XLSX.utils.aoa_to_sheet(data);

  // Ajustar ancho de columnas para que el contenido sea legible
  worksheet['!cols'] = schema.fields.map(field => {
    const headerLen = field.key.length;
    const typeLen = buildTypeLabel(field).length;
    const exampleLen = String(field.example).length;
    const maxLen = Math.max(headerLen, typeLen, exampleLen, 15);
    return { wch: Math.min(maxLen + 2, 40) };
  });

  // Congelar las primeras 3 filas
  worksheet['!freeze'] = { xSplit: 0, ySplit: 3, topLeftCell: 'A4', activePane: 'bottomLeft' };

  // Crear el libro
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, schema.name);

  // Nombre del archivo
  const fileName = `plantilla_${schema.id}_${new Date().toISOString().split('T')[0]}.xlsx`;

  // Descargar
  XLSX.writeFile(workbook, fileName);
}

/**
 * Construye la etiqueta visible en la fila de tipo.
 * Ej: "TEXTO (obligatorio)" | "NÚMERO (obligatorio)" | "ENUM: a, b, c"
 */
function buildTypeLabel(field: FieldSchema): string {
  const requiredLabel = field.required ? 'obligatorio' : 'opcional';

  switch (field.type) {
    case 'text':
      return `TEXTO (${requiredLabel})`;
    case 'number':
      return `NÚMERO (${requiredLabel})`;
    case 'date':
      return `FECHA YYYY-MM-DD (${requiredLabel})`;
    case 'email':
      return `EMAIL (${requiredLabel})`;
    case 'phone':
      return `TELÉFONO (${requiredLabel})`;
    case 'enum':
      return `OPCIONES: ${field.options?.join(' | ') || ''} (${requiredLabel})`;
    default:
      return `DESCONOCIDO (${requiredLabel})`;
  }
}

/**
 * Devuelve un resumen de los campos obligatorios para mostrar en la UI.
 */
export function getRequiredFieldsSummary(schema: EntitySchema): string {
  const required = schema.fields.filter(f => f.required);
  if (required.length === 0) return 'Ninguno';
  return required.map(f => f.label).join(', ');
}